import { defineStore, storeToRefs } from "pinia";
import { getMessaging, getToken, onMessage } from "firebase/messaging";
import capitalize from "lodash-es/capitalize";
import merge from "lodash-es/merge";
import type { RestNotification } from "@/types/entities/notifications";

export const useNotificationStore = defineStore(
  "notifications",
  () => {
    const { $firebaseApp: firebaseApp } = useNuxtApp();
    const messaging = getMessaging(firebaseApp);
    const { firebaseVapidKey } = useRuntimeConfig().public;

    const sessionStore = useSessionStore();
    const { isLoggedIn } = storeToRefs(sessionStore);
    const toast = useToast();
    const route = useRoute();
    const {
      updatePaginationOnScroll: updateNotificationPaginationOnScroll,
      pagination: notificationPagination,
      resetPagination: resetNotficationPagination,
    } = usePagination({ itemsPerPage: 20 });

    const notificationsAreAllowed = ref(false);
    async function confirmNotificationsAreAllowed() {
      if (!("Notification" in window)) {
        return;
      }
      if (notificationsAreAllowed.value) {
        return;
      } else {
        await Notification.requestPermission()
          .then((permission) => {
            // If the user accepts, let's create a notification
            if (permission === "granted") {
              notificationsAreAllowed.value = true;
            }
          })
          .catch((err) => {
            console.log(err);

            toast.error(err, {
              id: "notification-permission-fail-toast",
              timeout: 2000,
            });
          });
      }
    }

    async function getMessagingToken() {
      await isModuleReady(sessionStore);
      getToken(messaging, { vapidKey: firebaseVapidKey })
        .then(async (currentToken) => {
          if (currentToken) {
            try {
              await useRequest("users", {
                method: "PATCH",
                body: {
                  notificationToken: currentToken,
                },
              });
            } catch (error) {
              doNothing(error);
            }
          }
        })
        .catch((error) => {
          console.log({ error });
          // toast.error(String(error), { id: 'notification-token-fail-toast', timeout: 2000 })
        });
    }

    watchEffect(() => {
      if (!notificationsAreAllowed.value || !isLoggedIn.value) return;
      getMessagingToken();
    });

    const notificationsFetchKey = ref(Date.now());
    const notifications = ref<RestNotification[]>([]);
    const unreadNotificationsCount = ref(0);

    async function fetchNotifications() {
      await isModuleReady(sessionStore);
      if (!isLoggedIn.value) return [];
      try {
        const response = await useRequest<{
          data: {
            notifications: RestNotification[];
            unReadNotificationCount: number;
            meta: PaginationMeta;
          };
        }>("notifications", {
          params: {
            ...convertPaginationToParams(notificationPagination.value),
            isRead: false,
          },
        });

        const responseData = response._data?.data;
        unreadNotificationsCount.value =
          responseData?.unReadNotificationCount ?? 0;
        notifications.value =
          responseData?.notifications.filter((notification) => {
            return !notification.isRead;
          }) ?? [];
        merge(
          notificationPagination.value,
          formatPaginationmeta(responseData?.meta),
        );
      } catch (error) {
        doNothing(error);
        return [];
      }
    }

    watch(
      notificationsFetchKey,
      (value) => {
        if (!value) return;
        fetchNotifications();
      },
      { immediate: true },
    );

    debouncedWatch(
      notifications,
      () => {
        if (!notifications.value.length && unreadNotificationsCount.value) {
          notificationPagination.value = {
            ...notificationPagination.value,
            itemsPerPage: notificationPagination.value.itemsPerPage + 10,
          };
        }
      },
      {
        debounce: 500,
      },
    );

    watch(notificationPagination, () => {
      fetchNotifications();
    });

    async function markNotificationAsRead(notificationId: string) {
      notifications.value.splice(
        notifications.value.findIndex(
          (notification) => notification.id === notificationId,
        ),
        1,
      );
      try {
        await useRequest(`notifications/${notificationId}`);
        notificationsFetchKey.value = Date.now();
      } catch (error) {
        doNothing(error);
      }
    }

    onMessage(messaging, (payload) => {
      const notificationTitle = capitalize(payload.notification?.title ?? "");
      const notificationOptions = {
        body: payload.notification?.body ?? "",
        icon: payload.notification?.icon,
      };

      new Notification(notificationTitle, notificationOptions);
    });

    watch(
      () => route.path,
      () => {
        resetNotficationPagination();
      },
    );

    return {
      notifications,
      unreadNotificationsCount,
      updateNotificationPaginationOnScroll,
      notificationsAreAllowed,
      notificationsFetchKey,
      resetNotficationPagination,
      confirmNotificationsAreAllowed,
      markNotificationAsRead,
    };
  },
  {
    persistedState: {
      persist: false,
    },
  },
);
